import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;

  const filteredArticles = useMemo(() => {
    if (!slug) return [];
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <SEOHead title="分类未找到" description="您访问的分类不存在或已被移除。" />
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
            <h1 className="mt-4 text-2xl font-bold text-foreground-800 font-heading">分类未找到</h1>
            <p className="mt-2 text-foreground-500">您访问的分类不存在或已被移除。</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 黑料社区网`,
    description: category.description,
    url: `https://heiliao.shequ.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料社区网',
      url: 'https://heiliao.shequ.cn/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`${category.name},黑料社区网,爆料,八卦,吃瓜`}
        canonicalUrl={`https://heiliao.shequ.cn/category/${slug}`}
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className={`${category.icon} text-white text-lg`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
            </div>
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-6xl text-foreground-300"></i>
              <p className="mt-4 text-lg text-foreground-500">该分类下暂无文章</p>
              <p className="mt-1 text-sm text-foreground-400">更多精彩内容即将上线，敬请期待</p>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}